import React, { useState, useEffect } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  Drawer,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import FileCopyOutlinedIcon from "@material-ui/icons/FileCopyOutlined";
import MemoryIcon from "@material-ui/icons/Memory";
import SettingsOutlinedIcon from "@material-ui/icons/SettingsOutlined";
import RefreshIcon from "@material-ui/icons/Refresh";
import { format, parseISO } from "date-fns";
import { toast } from "react-toastify";

import { i18n } from "../../translate/i18n";
import {
  HISTORY_SCOPES,
  getHistoryScopeLabel,
} from "../../utils/chatAgentHistoryScope";

const drawerWidth = 320;

const useStyles = makeStyles((theme) => ({
  drawer: {
    width: drawerWidth,
    flexShrink: 0,
  },
  drawerPaper: {
    width: drawerWidth,
    display: "flex",
    borderTop: "1px solid rgba(0, 0, 0, 0.12)",
    borderLeft: "1px solid rgba(0, 0, 0, 0.12)",
    borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
    borderTopLeftRadius: 4,
    borderBottomLeftRadius: 4,
  },
  header: {
    display: "flex",
    borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
    backgroundColor: theme.palette.surface?.main || theme.palette.fancyBackground,
    alignItems: "center",
    padding: theme.spacing(0, 1),
    minHeight: 50,
    gap: theme.spacing(0.5),
  },
  content: {
    flex: 1,
    overflowY: "auto",
    padding: theme.spacing(1.5),
    ...theme.scrollbarStyles,
  },
  configBox: {
    padding: theme.spacing(1.5),
    marginBottom: theme.spacing(1.5),
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor:
      theme.mode === "light" ? "rgba(15, 23, 42, 0.03)" : "rgba(148, 163, 184, 0.08)",
  },
  section: {
    marginBottom: theme.spacing(2),
  },
  sectionLabel: {
    fontWeight: 600,
    fontSize: "0.75rem",
    textTransform: "uppercase",
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(0.5),
  },
  sectionValue: {
    fontSize: "0.875rem",
    lineHeight: 1.45,
    whiteSpace: "pre-wrap",
  },
  suggestedBox: {
    backgroundColor:
      theme.mode === "light" ? "rgba(37, 99, 235, 0.06)" : "rgba(37, 99, 235, 0.12)",
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(1.5),
    border: `1px solid ${theme.palette.divider}`,
  },
  historyItem: {
    padding: theme.spacing(1),
    borderRadius: theme.shape.borderRadius,
    cursor: "pointer",
    marginBottom: theme.spacing(0.5),
    border: `1px solid ${theme.palette.divider}`,
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
    },
  },
  historyItemActive: {
    borderColor: theme.palette.primary.main,
    backgroundColor:
      theme.mode === "light" ? "rgba(37, 99, 235, 0.08)" : "rgba(37, 99, 235, 0.15)",
  },
  emptyState: {
    textAlign: "center",
    color: theme.palette.text.secondary,
    padding: theme.spacing(2, 1),
  },
  chipRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(0.5),
    marginBottom: theme.spacing(1),
  },
  activeAgentChip: {
    marginBottom: theme.spacing(1),
  },
  refineBox: {
    marginTop: theme.spacing(1.5),
    padding: theme.spacing(1.5),
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor:
      theme.mode === "light" ? "rgba(15, 23, 42, 0.02)" : "rgba(148, 163, 184, 0.06)",
  },
}));

const interestColor = {
  caliente: "#dc2626",
  tibio: "#d97706",
  frio: "#64748b",
};

const AnalysisView = ({ analysis, classes, onCopy }) => {
  if (!analysis) return null;

  return (
    <>
      <Box className={classes.chipRow}>
        <Chip
          size="small"
          label={analysis.intencion}
          style={{ textTransform: "capitalize" }}
        />
        <Chip
          size="small"
          label={analysis.nivelInteres}
          style={{
            textTransform: "capitalize",
            backgroundColor: `${interestColor[analysis.nivelInteres] || "#64748b"}22`,
            color: interestColor[analysis.nivelInteres] || "#64748b",
          }}
        />
        <Chip
          size="small"
          label={`${analysis.probabilidadVenta}%`}
          color="primary"
          variant="outlined"
        />
      </Box>

      <Box className={classes.section}>
        <Typography className={classes.sectionLabel}>
          {i18n.t("ticketAiAnalysis.fields.resumen")}
        </Typography>
        <Typography className={classes.sectionValue}>{analysis.resumen}</Typography>
      </Box>

      <Box className={classes.section}>
        <Typography className={classes.sectionLabel}>
          {i18n.t("ticketAiAnalysis.fields.necesidadPrincipal")}
        </Typography>
        <Typography className={classes.sectionValue}>{analysis.necesidadPrincipal}</Typography>
      </Box>

      <Box className={classes.section}>
        <Typography className={classes.sectionLabel}>
          {i18n.t("ticketAiAnalysis.fields.proximoPaso")}
        </Typography>
        <Typography className={classes.sectionValue}>{analysis.proximoPaso}</Typography>
      </Box>

      <Box className={classes.section}>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={0.5}>
          <Typography className={classes.sectionLabel} style={{ marginBottom: 0 }}>
            {i18n.t("ticketAiAnalysis.fields.respuestaSugerida")}
          </Typography>
          <IconButton size="small" onClick={() => onCopy(analysis.respuestaSugerida)}>
            <FileCopyOutlinedIcon fontSize="small" />
          </IconButton>
        </Box>
        <Box className={classes.suggestedBox}>
          <Typography className={classes.sectionValue}>{analysis.respuestaSugerida}</Typography>
        </Box>
      </Box>
    </>
  );
};

const TicketAiAnalysisDrawer = ({
  open,
  onClose,
  analyzing,
  refining,
  onAnalyze,
  onRefine,
  analyses,
  selectedAnalysisId,
  onSelectAnalysis,
  chatAgentConfig,
  selectedPromptId,
  onPromptChange,
  historyScope,
  onHistoryScopeChange,
  onRefreshConfig,
}) => {
  const classes = useStyles();
  const [configOpen, setConfigOpen] = useState(false);
  const [refineInstruction, setRefineInstruction] = useState("");

  const isBusy = analyzing || refining;

  useEffect(() => {
    if (open && !chatAgentConfig?.configured) {
      setConfigOpen(true);
    }
  }, [open, chatAgentConfig?.configured]);

  const prompts = chatAgentConfig?.prompts || [];
  const isConfigured = Boolean(chatAgentConfig?.configured) || prompts.length > 0;
  const resolvedPromptId = selectedPromptId ? Number(selectedPromptId) : "";
  const activePrompt = prompts.find((p) => Number(p.id) === Number(resolvedPromptId));
  const resolvedHistoryScope = historyScope || "week";
  const historyScopeLabel = i18n.t(getHistoryScopeLabel(resolvedHistoryScope));

  const selectedAnalysis =
    analyses.find((item) => item.id === selectedAnalysisId) || analyses[0] || null;

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text || "");
    toast.success(i18n.t("ticketAiAnalysis.copied"));
  };

  const formatDate = (value) => {
    try {
      return format(parseISO(value), "dd/MM/yyyy HH:mm");
    } catch {
      return "";
    }
  };

  const handleToggleConfig = () => {
    setConfigOpen((prev) => !prev);
  };

  const handleRefine = () => {
    if (!selectedAnalysis?.id || !refineInstruction.trim()) return;
    onRefine(selectedAnalysis.id, refineInstruction);
  };

  useEffect(() => {
    setRefineInstruction("");
  }, [selectedAnalysisId]);

  return (
    <Drawer
      className={classes.drawer}
      variant="persistent"
      anchor="left"
      open={open}
      PaperProps={{ style: { position: "absolute" } }}
      BackdropProps={{ style: { position: "absolute" } }}
      ModalProps={{
        container: document.getElementById("drawer-container"),
        style: { position: "absolute" },
      }}
      classes={{ paper: classes.drawerPaper }}
    >
      <div className={classes.header}>
        <MemoryIcon color="primary" fontSize="small" />
        <Typography variant="subtitle2" style={{ flex: 1 }}>
          {i18n.t("ticketAiAnalysis.title")}
        </Typography>
        <IconButton
          size="small"
          onClick={handleToggleConfig}
          title={i18n.t("ticketAiAnalysis.settings")}
          color={configOpen ? "primary" : "default"}
        >
          <SettingsOutlinedIcon fontSize="small" />
        </IconButton>
        <IconButton size="small" onClick={onClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </div>

      <div className={classes.content}>
        <Collapse in={configOpen}>
          <Box className={classes.configBox}>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
              <Typography variant="subtitle2">
                {i18n.t("ticketAiAnalysis.settingsTitle")}
              </Typography>
              <IconButton size="small" onClick={onRefreshConfig} title={i18n.t("ticketAiAnalysis.refreshConfig")}>
                <RefreshIcon fontSize="small" />
              </IconButton>
            </Box>

            {isConfigured ? (
              <>
                <Typography variant="caption" color="textSecondary" display="block" gutterBottom>
                  {i18n.t("ticketAiAnalysis.settingsHint")}
                </Typography>
                <FormControl fullWidth variant="outlined" size="small" margin="dense">
                  <InputLabel>{i18n.t("ticketAiAnalysis.prompt")}</InputLabel>
                  <Select
                    value={resolvedPromptId}
                    onChange={(e) => onPromptChange(Number(e.target.value))}
                    label={i18n.t("ticketAiAnalysis.prompt")}
                  >
                    {prompts.map((prompt) => (
                      <MenuItem key={prompt.id} value={prompt.id}>
                        {prompt.name} ({prompt.model})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {activePrompt && (
                  <Chip
                    size="small"
                    color="primary"
                    variant="outlined"
                    className={classes.activeAgentChip}
                    label={i18n.t("ticketAiAnalysis.activeAgent", { name: activePrompt.name })}
                  />
                )}
                <Typography variant="caption" color="textSecondary" display="block" style={{ marginTop: 8 }}>
                  {i18n.t("ticketAiAnalysis.historyScopeHint")}
                </Typography>
                <FormControl fullWidth variant="outlined" size="small" margin="dense">
                  <InputLabel>{i18n.t("ticketAiAnalysis.historyScopeLabel")}</InputLabel>
                  <Select
                    value={resolvedHistoryScope}
                    onChange={(e) => onHistoryScopeChange(e.target.value)}
                    label={i18n.t("ticketAiAnalysis.historyScopeLabel")}
                  >
                    {HISTORY_SCOPES.map((scope) => (
                      <MenuItem key={scope.value} value={scope.value}>
                        {i18n.t(scope.labelKey)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </>
            ) : (
              <Box className={classes.emptyState} style={{ padding: "8px 0" }}>
                <Typography variant="body2" color="textSecondary" gutterBottom>
                  {i18n.t("ticketAiAnalysis.notConfigured")}
                </Typography>
                <Typography variant="caption" color="textSecondary" display="block">
                  {i18n.t("ticketAiAnalysis.notConfiguredSteps")}
                </Typography>
              </Box>
            )}
          </Box>
        </Collapse>

        {!configOpen && isConfigured && (
          <Box className={classes.chipRow}>
            {activePrompt && (
              <Chip
                size="small"
                variant="outlined"
                label={activePrompt.name}
                onClick={handleToggleConfig}
                clickable
              />
            )}
            <Chip
              size="small"
              variant="outlined"
              label={historyScopeLabel}
              onClick={handleToggleConfig}
              clickable
            />
          </Box>
        )}

        {isConfigured && (
          <Typography variant="caption" color="textSecondary" display="block" gutterBottom>
            {i18n.t("ticketAiAnalysis.willAnalyze", { scope: historyScopeLabel })}
          </Typography>
        )}

        <Button
          fullWidth
          variant="contained"
          color="primary"
          onClick={onAnalyze}
          disabled={isBusy || !isConfigured || !resolvedPromptId}
          startIcon={analyzing ? <CircularProgress size={18} color="inherit" /> : <MemoryIcon />}
          style={{ marginTop: 4, marginBottom: 12 }}
        >
          {analyzing
            ? i18n.t("ticketAiAnalysis.analyzing")
            : i18n.t("ticketAiAnalysis.analyze")}
        </Button>

        {!isConfigured && !configOpen && (
          <Box className={classes.emptyState}>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              {i18n.t("ticketAiAnalysis.openSettingsHint")}
            </Typography>
            <Button size="small" color="primary" onClick={handleToggleConfig}>
              {i18n.t("ticketAiAnalysis.settings")}
            </Button>
          </Box>
        )}

        {isBusy && (
          <Box display="flex" justifyContent="center" py={2}>
            <CircularProgress size={28} />
          </Box>
        )}

        {!isBusy && selectedAnalysis && (
          <>
            <Typography variant="caption" color="textSecondary" display="block" gutterBottom>
              {selectedAnalysis.promptName} · {formatDate(selectedAnalysis.createdAt)}
              {selectedAnalysis.userName ? ` · ${selectedAnalysis.userName}` : ""}
              {selectedAnalysis.refinedFrom
                ? ` · ${i18n.t("ticketAiAnalysis.refinedBadge")}`
                : ""}
            </Typography>
            {selectedAnalysis.agentInstruction && (
              <Typography variant="caption" color="textSecondary" display="block" gutterBottom>
                {i18n.t("ticketAiAnalysis.refineContext", {
                  text: selectedAnalysis.agentInstruction,
                })}
              </Typography>
            )}
            {selectedAnalysis.messageCount != null && (
              <Typography variant="caption" color="textSecondary" display="block" gutterBottom>
                {i18n.t("ticketAiAnalysis.analysisMeta", {
                  count: selectedAnalysis.messageCount,
                  scope: i18n.t(
                    getHistoryScopeLabel(selectedAnalysis.historyScope || "week")
                  ),
                })}
              </Typography>
            )}
            <AnalysisView
              analysis={selectedAnalysis.analysis}
              classes={classes}
              onCopy={handleCopy}
            />

            <Box className={classes.refineBox}>
              <Typography className={classes.sectionLabel}>
                {i18n.t("ticketAiAnalysis.refineTitle")}
              </Typography>
              <Typography variant="caption" color="textSecondary" display="block" gutterBottom>
                {i18n.t("ticketAiAnalysis.refineHint")}
              </Typography>
              <TextField
                fullWidth
                multiline
                minRows={2}
                maxRows={4}
                variant="outlined"
                size="small"
                placeholder={i18n.t("ticketAiAnalysis.refinePlaceholder")}
                value={refineInstruction}
                onChange={(e) => setRefineInstruction(e.target.value)}
                disabled={refining}
              />
              <Button
                fullWidth
                variant="outlined"
                color="primary"
                size="small"
                onClick={handleRefine}
                disabled={refining || !refineInstruction.trim()}
                style={{ marginTop: 8 }}
              >
                {refining
                  ? i18n.t("ticketAiAnalysis.refining")
                  : i18n.t("ticketAiAnalysis.refine")}
              </Button>
            </Box>
          </>
        )}

        {!isBusy && !selectedAnalysis && isConfigured && (
          <Typography className={classes.emptyState}>
            {i18n.t("ticketAiAnalysis.empty")}
          </Typography>
        )}

        {analyses.length > 1 && (
          <>
            <Divider style={{ margin: "12px 0" }} />
            <Typography className={classes.sectionLabel}>
              {i18n.t("ticketAiAnalysis.history")}
            </Typography>
            {analyses.map((item) => (
              <Box
                key={item.id}
                className={`${classes.historyItem} ${
                  item.id === selectedAnalysis?.id ? classes.historyItemActive : ""
                }`}
                onClick={() => onSelectAnalysis(item.id)}
              >
                <Typography variant="body2" noWrap>
                  {item.analysis?.resumen || item.promptName}
                  {item.refinedFrom ? ` (${i18n.t("ticketAiAnalysis.refinedBadge")})` : ""}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  {formatDate(item.createdAt)}
                </Typography>
              </Box>
            ))}
          </>
        )}
      </div>
    </Drawer>
  );
};

export default TicketAiAnalysisDrawer;
